/**
 * User preferences over the planner's interval-session bank (workoutBank.ts):
 *  - which built-in/custom sessions are FAVORITES (preferred by the picker), and
 *  - extra CUSTOM sessions the athlete added when they felt one was missing.
 *
 * Stored in localStorage. Custom sessions are serializable data (no functions);
 * toBankSession() turns them into real BankSession candidates with a generate()
 * so the planner can pick them. Favorites/custom flow into generateTrainingPlan
 * via opts (see PRESENTER/trainingPlanPage.tsx) and bias pickIntervalSession.
 */
import { SportType } from "../types/training";
import { BankSession, Focus, RaceDistance, ALL_PHASES } from "./workoutBank";

export interface CustomSession {
  id: string;
  name: string;
  sport: SportType;
  focus: Focus;
  distances: RaceDistance[]; // which race distances it suits
  minCTL: number;            // fitness floor
  body: string;             // intervals.icu-style workout text
}

const FAV_KEY = "interval-bank-favorites";
const CUSTOM_KEY = "interval-bank-custom";

const ALL_DISTANCES: RaceDistance[] = ["5k", "10k", "half", "marathon", "ultra", "unknown"];

// ── Favorites ────────────────────────────────────────────────────────────────

export function loadFavoriteIds(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(x => typeof x === "string") : [];
  } catch { return []; }
}

export function toggleFavoriteId(id: string): string[] {
  const cur = loadFavoriteIds();
  const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
  try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  return next;
}

// ── Custom sessions ──────────────────────────────────────────────────────────

export function loadCustomSessions(): CustomSession[] {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function saveCustomSessions(list: CustomSession[]): void {
  try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

export function addCustomSession(s: Omit<CustomSession, "id">): CustomSession[] {
  const id = `custom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const next = [...loadCustomSessions(), {
    ...s,
    id,
    distances: s.distances.length > 0 ? s.distances : ALL_DISTANCES,
  }];
  saveCustomSessions(next);
  return next;
}

export function removeCustomSession(id: string): CustomSession[] {
  const next = loadCustomSessions().filter(s => s.id !== id);
  saveCustomSessions(next);
  return next;
}

// ── Conversion ───────────────────────────────────────────────────────────────

/** Ski sessions cover both NordicSki and RollerSki; everything else is literal. */
function sportsFor(sport: SportType): SportType[] {
  return sport === "NordicSki" || sport === "RollerSki"
    ? ["NordicSki", "RollerSki"]
    : [sport];
}

/** Turn stored custom data into a real bank candidate the picker can choose.
 *  generate() returns the body verbatim — makeSession re-parses it for duration
 *  and TSS, so a fixed-length custom workout still slots into the plan. */
export function toBankSession(c: CustomSession): BankSession {
  return {
    id: c.id,
    name: c.name,
    rationale: "Eget pass.",
    sports: sportsFor(c.sport),
    phases: ALL_PHASES,
    distances: c.distances.length > 0 ? c.distances : ALL_DISTANCES,
    minCTL: c.minCTL,
    focus: c.focus,
    custom: true,
    generate: () => c.body,
  };
}

/** All custom sessions as bank candidates — pass to generateTrainingPlan. */
export function customBankSessions(): BankSession[] {
  return loadCustomSessions().map(toBankSession);
}
